<script lang="ts">
  import { onMount, untrack } from 'svelte'

  import { getBasemap, resolveBasemap } from '../map/basemaps'
  import { LayerController } from '../map/controller'
  import { createMap, type MapHandle } from '../map/createMap'
  import { ApiError, fetchPlace } from '../api/client'
  import type { Place } from '../api/types'
  import { probePoint, type MdHit, type PointProbe } from '../map/layers/probe'
  import { setModelWidthTarget } from '../map/layers/modelLayer'
  import {
    FRAMES_DESKTOP,
    FRAMES_MODEL_DESKTOP,
    FRAMES_MODEL_PHONE,
    FRAMES_PHONE,
    getLayerDef,
    groundFor,
    setFrameBudget,
    setModelFrameBudget,
  } from '../map/layers/registry'
  import { alertLink, takeAlertRequest } from '../state/alertLink.svelte'
  import { applyControllerState, layers, opacityOf, setPlaying } from '../state/layers.svelte'
  import { layout, setView } from '../state/layout.svelte'
  import Icon from './Icon.svelte'
  import Toast from './pwa/Toast.svelte'
  import { location, select } from '../state/location.svelte'
  import { models, modelSettingsKey } from '../state/models.svelte'
  import { prefs } from '../state/prefs.svelte'
  import { theme } from '../state/theme.svelte'
  import { whileVisible } from '../visibility'
  import AlertPopup, { type AlertRef } from './AlertPopup.svelte'
  import Legend from './Legend.svelte'
  import MapMenu from './MapMenu.svelte'
  import MdPopup from './MdPopup.svelte'
  import TimeSlider from './TimeSlider.svelte'

  interface Props {
    cartoKey: string
    /**
     * What the app's own chrome covers on each side of the map, in px, so a
     * flight centres the point on the map that is left showing. On the
     * desktop the map runs under the rail and the panel, and `left` is their
     * width (Phase 11 plan §7).
     */
    padding?: { top: number; bottom: number; left?: number; right?: number }
    /**
     * The phone's other sections cover the map (Phase 11 plan §5.3). It stays
     * mounted and laid out at its own size, so a return re-creates nothing;
     * while hidden the loop pauses and the live overlays stop refreshing, and
     * a return refreshes them at once. Popups stay visible: an alert row on
     * Forecast opens its popup over the forecast.
     */
    hidden?: boolean
    /**
     * The camera's bearing and pitch, in degrees, for the layout's compass
     * (issue #5): MapLibre lets a right-drag, a ctrl-drag or a two-finger
     * twist turn and tilt the map, and nothing else in our chrome undoes it.
     */
    bearing?: number
    pitch?: number
  }

  let {
    cartoKey,
    padding = { top: 0, bottom: 0 },
    hidden = false,
    bearing = $bindable(0),
    pitch = $bindable(0),
  }: Props = $props()

  /** The desktop's own zoom buttons (MapLibre's are not used, plan §7). */
  export function zoomIn(): void {
    handle?.map.zoomIn()
  }

  export function zoomOut(): void {
    handle?.map.zoomOut()
  }

  /** The compass: back to north-up and flat, where the map is. */
  export function resetNorth(): void {
    handle?.resetNorth(!layout.reducedMotion)
  }

  let container: HTMLDivElement
  let handle: MapHandle | null = $state(null)
  let controller: LayerController | null = null
  /**
   * The basemap never loaded (round 2's 3.7). Only an error before the first
   * `style.load` counts: MapLibre sends every failed overlay tile to the same
   * `error` event, and one nowCOAST tile answering 500 is not "the map didn't
   * load" — those are logged and the layer's own retry handles them.
   */
  let mapFailed = $state(false)
  let styleLoaded = false
  let attempts = $state(0)
  let basemapHost = $state('')
  /** Where the last tap landed, in CSS pixels within the container. */
  let menu = $state<{ x: number; y: number; lat: number; lon: number } | null>(null)
  let probe = $state<PointProbe | null>(null)
  let place = $state<Place | null>(null)
  /** The place lookup has answered, with a name or without one. */
  let placeSettled = $state(false)
  /** NWS said 404: no point forecast here (the ocean, Mexico). */
  let noCoverage = $state(false)
  /** `fromLink`: opened by id from a notification or a link, not from the menu. */
  let openAlert = $state<(AlertRef & { fromLink?: boolean }) | null>(null)
  let openMd = $state<MdHit | null>(null)
  let probeSeq = 0

  // Zoom for a searched or geolocated point: county-level, which is roughly the
  // area one NWS forecast covers.
  const POINT_ZOOM = 9

  // How often a visible map asks its live overlays whether upstream has moved.
  // Radar gains a frame about every four minutes and the capabilities read
  // behind this is cached for four (`timeSource.ts`), so a new frame shows up
  // within six minutes and nowCOAST is asked at most once per four.
  const LIVE_REFRESH_MS = 2 * 60_000

  onMount(() => {
    // Read once, at construction: the frame budget is baked into each layer as
    // it is created, and 12 live WMS renders per frame is not a phone's job.
    setFrameBudget(layout.isDesktop ? FRAMES_DESKTOP : FRAMES_PHONE)
    // Our own plates get their own budget, an order of magnitude larger.
    // nowCOAST's 12 pays for a live render at NOAA's end; these are immutable
    // PNGs behind a CacheFirst rule on one swapped texture, and 12 of them
    // drew the 15-minute product at 1h45m spacing. See `registry.ts`.
    setModelFrameBudget(layout.isDesktop ? FRAMES_MODEL_DESKTOP : FRAMES_MODEL_PHONE)
    // Same decision, different budget: an `image` source has no viewport
    // culling, so a phone asking for the 2048 px frames carries a whole CONUS
    // texture it cannot see. The manifest lists what exists and the closest
    // width to this wins.
    setModelWidthTarget(layout.isDesktop ? 2048 : 1024)

    handle = createMap(container, {
      basemap: resolveBasemap(prefs.basemap, theme.resolved, groundFor(layers.active)),
      cartoKey,
      compact: !layout.isDesktop,
      onError: (message) => {
        if (styleLoaded) {
          console.warn('map error', message)
          return
        }
        mapFailed = true
        console.warn('the basemap did not load', message)
      },
    })

    controller = new LayerController(handle.map, applyControllerState)

    const offStyle = handle.onStyleLoad(() => {
      styleLoaded = true
      mapFailed = false
    })

    // A tap asks the map what is there; it does not move the forecast. That is
    // the menu's "Forecast here" button, so tapping an alert polygon no longer
    // reloads the sidebar as a side effect.
    const offClick = handle.onClick((lat, lon, point) => {
      openAlert = null
      openMd = null
      menu = { x: point.x, y: point.y, lat, lon }
      void runProbe(point, lat, lon)
    })

    // The menu is anchored to a pixel, so a pan or zoom makes it a lie.
    const offMove = handle.onMoveStart(closeMenu)

    const offRotate = handle.onRotate((b, p) => {
      bearing = b
      pitch = p
    })

    // The container changes size when the layout flips at 900 px and when the
    // browser chrome slides away on a phone; MapLibre only notices if told.
    const observer = new ResizeObserver(() => handle?.resize())
    observer.observe(container)

    // A background tab throttles the play loop to nothing anyway; stopping it
    // explicitly means it does not resume without the user asking.
    const onHide = () => setPlaying(false)
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onHide)

    // Frames are fetched when a layer is turned on and, before this, never
    // again: a map left open, or a phone picked back up, showed the loop from
    // whenever the layer was switched on.
    // Not while another section covers the map; the return refreshes instead.
    const stopRefresh = whileVisible(() => {
      if (!untrack(() => hidden)) void controller?.refresh()
    }, LIVE_REFRESH_MS)

    return () => {
      stopRefresh()
      offStyle()
      offClick()
      offMove()
      offRotate()
      observer.disconnect()
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onHide)
      controller?.destroy()
      controller = null
      handle?.destroy()
      handle = null
    }
  })

  function closeMenu() {
    menu = null
    probe = null
    place = null
    placeSettled = false
    noCoverage = false
    probeSeq++ // Drop an in-flight probe rather than let it repopulate the menu.
  }

  async function runProbe(point: { x: number; y: number }, lat: number, lon: number) {
    if (!handle) return
    const seq = ++probeSeq
    probe = null
    place = null
    placeSettled = false
    noCoverage = false

    // Each layer's own frame, not the slider's position: see probe.ts.
    const times = controller?.currentTimes() ?? new Map()
    // In parallel, and each lands on its own: the name should not wait on a
    // WMS round trip, and the readouts should not wait on NWS.
    // A layer the slider has switched off past now draws nothing, so it says
    // nothing either.
    const shown = layers.active.filter((id) => !controller?.isHiddenPastNow(id))
    void probePoint(handle.map, shown, point, times, {
      lat,
      lon,
      layers: controller?.activeLayers(),
    }).then((found) => {
      if (seq === probeSeq) probe = found
    })
    // A point over the ocean or outside the US has no name; the menu falls
    // back to coordinates, which it shows either way. Anything else is a bug
    // somewhere — a stale backend without the route, most likely — and a
    // silently unnamed point gives nobody a thread to pull.
    void fetchPlace(lat, lon)
      .then((found) => {
        if (seq === probeSeq) place = found
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          if (seq === probeSeq) noCoverage = true
          return
        }
        console.warn('place lookup failed; showing coordinates only', e)
      })
      .finally(() => {
        if (seq === probeSeq) placeSettled = true
      })
  }

  // On a phone the menu is only reachable from Map, so the point's forecast
  // is shown rather than changed out of sight (plan §2.3).
  function forecastHere() {
    if (!menu) return
    void select({ lat: menu.lat, lon: menu.lon, source: 'click' })
    closeMenu()
    setView('forecast')
  }

  // Follow the selection (plan §5.7). Nothing the boot chose animates: a
  // restored point, a deep link, and a first fix with no placeholder before it
  // all jump, because a flight from the middle of the country is just noise.
  // A boot fix that moved off its placeholder flies, as a visible correction.
  let shownAny = false // Not $state: only this effect reads it.
  $effect(() => {
    const point = location.selected
    if (!handle || !point) return
    handle.setMarker(point.lat, point.lon)
    const first = !shownAny
    shownAny = true
    // A map click needs no camera move; the user put the point where they
    // meant. A fix on return needs none either: the user may have panned
    // somewhere on purpose, and a refresh never yanks the map.
    if (point.source === 'click' || point.source === 'here-refresh') return
    const booting =
      point.source === 'restore' || point.source === 'link' || (point.source === 'here' && first)
    if (booting || layout.reducedMotion) {
      handle.jumpTo(point.lat, point.lon, POINT_ZOOM)
    } else {
      handle.flyTo(point.lat, point.lon, POINT_ZOOM)
    }
  })

  // createMap already applied one style; re-applying it on mount would refetch
  // the whole basemap. The key is part of the identity because /api/config can
  // land after boot, and the CARTO style embeds it in every tile URL.
  let appliedStyle = '' // Deliberately not $state: only this effect reads it.
  $effect(() => {
    // `auto` follows the theme, so an OS flip is a basemap switch too, and so
    // is a model plate going on or off on the dark theme: a plate needs
    // Positron under it (Phase 10 plan §7.1).
    const basemap = resolveBasemap(prefs.basemap, theme.resolved, groundFor(layers.active))
    const wanted = `${basemap}:${cartoKey}`
    if (!handle) return
    if (appliedStyle === '') {
      appliedStyle = wanted
      return
    }
    if (appliedStyle === wanted) return
    appliedStyle = wanted
    // Tell the controller first: setStyle leaves the map styleless for a beat,
    // and it re-adds everything on the map's own style.load.
    controller?.suspend()
    handle.setBasemap(basemap, cartoKey)
  })

  // The overlay stack. `layers.active` is a $state proxy, so it is a different
  // array object on every read; join it into a key and compare that instead.
  $effect(() => {
    const active = [...layers.active]
    controller?.setActive(active)
    // Opacity is pushed after activation so a newly added layer gets the user's
    // stored value rather than the registry default.
    for (const id of active) controller?.setOpacity(id, opacityOf(id))
  })

  // The model run and the lead window are read when a layer is *created* — a
  // frame list is fetched at `load()` and there is nothing to mutate later —
  // so a change to either rebuilds the model layers rather than telling them.
  // `setActive` cannot do it: its first act is to return early when the id
  // list has not changed, which is exactly this case.
  let modelKey = $derived(modelSettingsKey())
  let appliedModelKey = '' // Not $state: only this effect reads it.
  $effect(() => {
    const wanted = modelKey
    if (!controller) return
    if (appliedModelKey === '') {
      appliedModelKey = wanted
      return
    }
    if (appliedModelKey === wanted) return
    appliedModelKey = wanted
    controller.reload((id) => Boolean(getLayerDef(id)?.model))
  })

  // The catalog moved: a run finished, `latest` advanced, or a run that is
  // still publishing gained hours. Only the models named in `changed` are
  // rebuilt — `reload()` drops and re-creates a layer, and doing that to a GFS
  // plate because HRRR gained an hour is a texture re-fetch for nothing. The
  // revision only bumps on a material change (see `note` in
  // `state/models.svelte.ts`), so this runs a few times an hour rather than
  // once a minute.
  let appliedRevision = -1 // Not $state: only this effect reads it.
  $effect(() => {
    const revision = models.revision
    const changed = new Set(models.changed)
    if (!controller) return
    if (appliedRevision < 0) {
      appliedRevision = revision
      return
    }
    if (appliedRevision === revision) return
    appliedRevision = revision
    controller.reload((id) => {
      const model = getLayerDef(id)?.model
      return Boolean(model && changed.has(model))
    })
  })

  $effect(() => {
    controller?.setTimeIndex(layers.index)
  })

  $effect(() => {
    if (layers.playing) controller?.play()
    else controller?.pause()
  })

  // Reduced motion: never auto-play. Stepping the slider still works.
  $effect(() => {
    if (layout.reducedMotion && layers.playing) setPlaying(false)
  })

  $effect(() => {
    handle?.setPadding({
      top: padding.top,
      bottom: padding.bottom,
      left: padding.left ?? 0,
      right: padding.right ?? 0,
    })
  })

  // Covered: a loop nobody can see stops, and the menu about a pixel goes.
  // Shown again: whatever moved upstream meanwhile is asked for at once, the
  // rule `whileVisible` applies to a tab coming back.
  let wasHidden = false // Not $state: only this effect reads it.
  $effect(() => {
    const now = hidden
    if (now === wasHidden) return
    wasHidden = now
    untrack(() => {
      if (now) {
        setPlaying(false)
        closeMenu()
      } else {
        void controller?.refresh()
      }
    })
  })

  // All three are answers about a stack that no longer exists. An alert
  // opened from a notification is not one, and stays.
  $effect(() => {
    void layers.active
    closeMenu()
    if (!untrack(() => openAlert?.fromLink)) openAlert = null
    openMd = null
  })

  // A notification tapped, a link with `&alert=` (plan §9.6), or an alert
  // row in the forecast.
  $effect(() => {
    if (!alertLink.capId) return
    const request = takeAlertRequest()
    if (!request) return
    untrack(() => {
      closeMenu()
      openMd = null
      openAlert = { capId: request.capId, alert: request.alert ?? undefined, fromLink: true }
    })
  })


  /** Ask for the basemap again, and say where it was asked. */
  function tryAgain() {
    if (!handle) return
    attempts += 1
    mapFailed = false
    const id = resolveBasemap(prefs.basemap, theme.resolved, groundFor(layers.active))
    const style = getBasemap(id).style(cartoKey)
    try {
      basemapHost = typeof style === 'string' ? new URL(style).host : ''
    } catch {
      basemapHost = ''
    }
    handle.setBasemap(id, cartoKey)
  }
</script>

<div class="map-wrap" class:offscreen={hidden}>
  <div class="map" bind:this={container}></div>

  <!-- One card along the bottom: the slider, then the legend (the
       redesign's `.timeline`). On a phone the legend's strip is under a
       hairline; on the desktop it is at the slider's right end, or under it
       when the map is too narrow for one row. Two components, so a layer
       with a legend and no timeline (the SPC products) still gets its strip
       and a timeline with no legend still gets its slider; the card hides
       when neither draws. -->
  <div class="map-card" class:compact={!layout.isDesktop}>
    <div class="card-row">
      <TimeSlider />
      <Legend compact={!layout.isDesktop} creditFor={(id) => controller?.attributionFor(id) ?? null} />
    </div>
  </div>

  {#if menu}
    <MapMenu
      x={menu.x}
      y={menu.y}
      lat={menu.lat}
      lon={menu.lon}
      {probe}
      {place}
      {placeSettled}
      outside={noCoverage}
      onForecast={forecastHere}
      onAlert={(alert) => {
        openAlert = alert
        openMd = null
        closeMenu()
      }}
      onMd={(md) => {
        openMd = md
        openAlert = null
        closeMenu()
      }}
      onClose={closeMenu}
    />
  {/if}

  {#if openAlert}
    <AlertPopup feature={openAlert} onClose={() => (openAlert = null)} />
  {/if}

  {#if openMd}
    <MdPopup md={openMd} onClose={() => (openMd = null)} />
  {/if}

  {#if mapFailed}
    <!-- The forecast does not need the map, and says so. -->
    <div class="map-error" role="alert">
      <Icon name="triangle-alert" size={22} strokeWidth={2.5} />
      <span class="map-error-title">The map didn't load</span>
      <span class="map-error-text">
        The basemap server isn't answering. The forecast and alerts {layout.isDesktop
          ? 'in the panel'
          : 'under Forecast'} still work.
      </span>
      <span class="map-error-actions">
        <button type="button" class="wx-btn ink touch-target" onclick={tryAgain}>
          <Icon name="refresh-cw" size={14} strokeWidth={2.5} />Try again
        </button>
        {#if attempts > 0}
          <span class="map-error-note"
            >Tried {attempts + 1} times{basemapHost ? ` · ${basemapHost}` : ''}</span
          >
        {/if}
      </span>
    </div>
  {/if}

  <Toast />
</div>

<style>
  .map-wrap {
    position: relative;
    height: 100%;
    width: 100%;
  }

  .map {
    position: absolute;
    inset: 0;
  }

  /* Hidden, never undisplayed: MapLibre keeps its size and its canvas. Only
     the map and its card, so a popup opened from another section shows. */
  .offscreen > .map,
  .offscreen > .map-card {
    visibility: hidden;
  }

  .map-card {
    position: absolute;
    /* On the desktop the map runs under the rail and the panel, and the
       layout says where the open map starts. */
    left: var(--chrome-left, 12px);
    right: 12px;
    /* 1B puts the card 12 px off the bottom; this is 40, because MapLibre's
       scale bar and the attribution (which OpenFreeMap and CARTO require to
       stay visible, and which a phone opens expanded at 28 px plus its 10 px
       margin) live in that band. */
    bottom: 40px;
    z-index: 15;
    background: var(--color-bg);
    box-shadow: var(--float-shadow);
    container-type: inline-size;
  }

  .card-row {
    display: flex;
    flex-direction: column;
  }

  .card-row > :global(.legend) {
    border-top: 1px solid var(--color-divider);
  }

  /* The desktop with room for both: the legend at the slider's right end,
     split from it by a hairline (the redesign's `.timeline`). */
  @container (min-width: 760px) {
    .map-card:not(.compact) .card-row {
      flex-direction: row;
      align-items: center;
    }

    .map-card:not(.compact) .card-row > :global(*:first-child) {
      flex: 1 1 auto;
      min-width: 0;
    }

    .map-card:not(.compact) .card-row > :global(.legend) {
      flex: 0 1 auto;
      max-width: 50%;
      align-self: stretch;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-top: 0;
      border-left: 1px solid var(--color-divider);
    }

    /* The name wraps rather than being cut: "Radar (base reflectivity)"
       beside a 180 px ramp is wider than half a 1280 px laptop's card. */
    .map-card:not(.compact) .card-row > :global(.legend .title) {
      white-space: normal;
      min-width: 72px;
    }
  }

  /* Neither half has anything to draw: no card, rather than an empty shadow. */
  .map-card:not(:has(.card-row > *)) {
    display: none;
  }

  .map-error {
    position: absolute;
    left: calc(var(--chrome-left, 12px) + 36px);
    top: 120px;
    z-index: 20;
    width: 380px;
    max-width: calc(100% - 32px);
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 22px;
    background: var(--color-bg);
    color: var(--color-text);
    border: 2px solid var(--color-text);
  }

  :global(.mobile) .map-error {
    left: 16px;
    right: 16px;
    top: calc(var(--controls-bottom, 0px) + 12px);
    width: auto;
    padding: 18px;
    gap: 8px;
  }

  .map-error-title {
    font-size: 18px;
    font-weight: 800;
  }

  .map-error-text {
    font-size: 13px;
    line-height: 1.5;
  }

  .map-error-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 4px;
    --btn-h: 34px;
  }

  .map-error-note {
    font-size: 12px;
    color: var(--color-muted);
  }
</style>
